import * as React from 'react'
import { ReactElement, useEffect, useRef, useState } from 'react'
import { CommandBarButton, css, getId, mergeStyleSets } from '@fluentui/react'
import Dropzone, { DropzoneRef } from 'react-dropzone'
import ImportDocumentDialog from '../../Dialog/ImportDocument/ImportDocumentDialog'
import { useBoolean, useConst, useMount } from '@fluentui/react-hooks'
import { IFolderOverviewItemDto, IRootOverviewItemDto } from '../../../config/documentConfig'
import Tooltip from '../../../components/Tooltip'
import { useTranslation } from 'react-i18next'

type Props = {
    folders: IRootOverviewItemDto
    selectedFolder?: IFolderOverviewItemDto
}

const styles = mergeStyleSets({
    backdrop: {
        position: 'fixed',
        display: 'none',
        zIndex: 999,
        left: 0,
        top: 0,
        width: '100%',
        height: '100%',
        background: 'rgba(192, 192, 255, .5)',
        selectors: {
            '&.is-visible': {
                display: 'block',
            },
        },
    },
})

export default function ImportDocumentButton(props: Props): ReactElement {
    const dropzoneRef = useRef<DropzoneRef>()
    const [visible, { setTrue: openDialog, setFalse: closeDialog }] = useBoolean(false)
    const [backdrop, { setTrue: openBackdrop, setFalse: closeBackdrop }] = useBoolean(false)
    const { t: translate } = useTranslation()

    useEffect(() => {
        if (visible) {
            closeBackdrop()
        }
    }, [visible])

    const [documents, setDocuments] = useState<File[]>()
    const id = useConst(() => getId('dropzone'))

    useMount(() => {
        window.addEventListener('dragenter', openBackdrop)
        window.addEventListener('dragleave', event => {
            if (event.target === document.getElementById(id) || event.target === document) {
                closeBackdrop()
            }
        })
    })

    return (
        <>
            <Tooltip hostProps={{ content: translate('file.pivot.document.import.button') }}>
                <CommandBarButton
                    disabled={!props.folders}
                    iconProps={{ iconName: 'CloudUpload' }}
                    onClick={() => dropzoneRef.current?.open()}
                    styles={{ root: { height: '100%' } }}
                />
            </Tooltip>
            {props.folders && (
                <Dropzone
                    ref={dropzoneRef}
                    onDropAccepted={(documents: File[]) => {
                        setDocuments(documents)
                        openDialog()
                    }}
                >
                    {({ getRootProps, getInputProps }) => (
                        <div
                            {...getRootProps()}
                            id={`${id}`}
                            className={css(styles.backdrop, backdrop && 'is-visible')}
                            onClick={closeBackdrop}
                            onContextMenu={closeBackdrop}
                        >
                            <input {...getInputProps()} />
                        </div>
                    )}
                </Dropzone>
            )}
            <ImportDocumentDialog
                folders={props.folders}
                selectedFolder={props.selectedFolder}
                documents={documents}
                hidden={!visible || !props.folders}
                onDismiss={closeDialog}
            />
        </>
    )
}
